import { AccountDetails, StatementRow, accountDetails } from './csvImport'

/**
 * A PDF statement as the server read it (SM-23), turned into the same rows a
 * CSV becomes, so the review step treats both alike.
 */

export interface PdfTransaction {
  date: string | null
  name: string
  reference: string
  in: number | null
  out: number | null
  balance: number | null
  /** The counterparty on its own, where the bank prints it apart (Starling). */
  payee?: string | null
}

export interface PdfStatement {
  bank: string
  openingBalance: number | null
  closingBalance: number | null
  /** As printed, where the bank prints them (HSBC). */
  sortCode?: string | null
  accountNumber?: string | null
  transactions: PdfTransaction[]
}

export interface PdfReadResult {
  rows: StatementRow[]
  /** Transactions with no date or no amount. */
  unreadable: number
  /**
   * Printed balances that don't follow from the transactions before them,
   * the closing balance included: a sign the PDF was misread.
   */
  balanceMismatches: number
  /** The account the statement is for, where it says (SM-25). */
  account?: AccountDetails
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export function rowsFromPdf(statement: PdfStatement): PdfReadResult {
  const rows: StatementRow[] = []
  let unreadable = 0
  for (const t of statement.transactions) {
    if (!t.date || !ISO_DATE.test(t.date) || (t.in === null && t.out === null)) {
      unreadable++
      continue
    }
    const name = t.name.replace(/\s+/g, ' ').trim()
    const description = [name, t.reference].join(' ').replace(/\s+/g, ' ').trim()
    // A payee only where the bank gives one: an HSBC name is the bank's raw
    // text ("TESCO STORES 3125"), and a payee is taken as the clean name.
    const payee = t.payee?.replace(/\s+/g, ' ').trim()
    rows.push({
      date: t.date,
      description: description || 'Unnamed transaction',
      amount: round2(Math.abs(t.in ?? 0) - Math.abs(t.out ?? 0)),
      ...(t.balance !== null ? { balance: round2(t.balance) } : {}),
      ...(name ? { merchantText: name } : {}),
      ...(payee ? { payee } : {}),
    })
  }
  const account = statement.sortCode && statement.accountNumber ? accountDetails(statement.sortCode, statement.accountNumber) : null
  return {
    rows,
    unreadable,
    balanceMismatches: balanceMismatches(statement.openingBalance, rows, statement.closingBalance),
    ...(account ? { account } : {}),
  }
}

/** Counted in pence, so floating point can't invent a mismatch. */
export function balanceMismatches(opening: number | null, rows: StatementRow[], closing: number | null): number {
  if (opening === null) return 0
  let running = pence(opening)
  let mismatches = 0
  for (const row of rows) {
    running += pence(row.amount)
    if (row.balance === undefined) continue
    if (pence(row.balance) !== running) {
      mismatches++
      // Carry on from what the bank printed, so one misread row is one mismatch.
      running = pence(row.balance)
    }
  }
  if (closing !== null && pence(closing) !== running) mismatches++
  return mismatches
}

const pence = (n: number) => Math.round(n * 100)
const round2 = (n: number) => Math.round(n * 100) / 100
